import api from "@/services/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Moon, Lock, CreditCard, Send, ChevronRight, Shield } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useDispatch } from "react-redux";
import { fetchUserProfile } from "@/store/slices/authSlice";
import { getImageUrl } from "@/utils/imageUrl";

// Sub-components
import Membership from "./Membership";
import Contribute from "./Contribute";
import ProfileInfo from "./ProfileInfo";
import Preferences from "./Preferences";
import Account from "./Account";
import ImageCropper from "@/components/common/ImageCropper";

// Helper to safely get location string
const getLocationString = (loc) => {
  if (!loc) return "";
  if (typeof loc === "object") {
    return loc.city || loc.name || "";
  }
  return loc;
};

const Settings = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("profile");

  // Save confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [changes, setChanges] = useState({});
  const [formData, setFormData] = useState({
    username: user?.username || "",
    name: user?.name || "",
    location: getLocationString(user?.location),
    date_of_birth: user?.date_of_birth || "",
    bio: user?.bio || "",
    gender: user?.gender || "",
    show_profile_public: user?.show_profile_public ?? true,
  });

  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");
  const [coverPreview, setCoverPreview] = useState(user?.cover_photo_url || "");
  // Selected files (not yet uploaded)
  const [newAvatarFile, setNewAvatarFile] = useState(null);
  const [newCoverFile, setNewCoverFile] = useState(null);

  // States for Cropping
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropType, setCropType] = useState(null); // 'avatar' or 'cover'

  useEffect(() => {
    if (user?.avatar_url) {
      setAvatarPreview(getImageUrl(user.avatar_url));
    } else {
      setAvatarPreview("");
    }

    if (user?.cover_photo_url) {
      setCoverPreview(getImageUrl(user.cover_photo_url));
    } else {
      setCoverPreview("");
    }
  }, [user]);

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Use cropper instead of directly setting
    setImageToCrop(URL.createObjectURL(file));
    setCropType("avatar");
    e.target.value = null; // reset input
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageToCrop(URL.createObjectURL(file));
    setCropType("cover");
    e.target.value = null; // reset input
  };

  const handleCropDone = (croppedBlob) => {
    const file = new File([croppedBlob], `${cropType}_cropped.jpeg`, {
      type: "image/jpeg",
    });

    if (cropType === "avatar") {
      setAvatarPreview(URL.createObjectURL(file));
      setNewAvatarFile(file);
    } else if (cropType === "cover") {
      setCoverPreview(URL.createObjectURL(file));
      setNewCoverFile(file);
    }

    setImageToCrop(null);
    setCropType(null);
  };



  // Compare old user data with formData
  const reviewChangesBeforeSave = () => {
    const changed = {};

    // check each field
    if (formData.username !== user?.username)
      changed.username = { from: user?.username, to: formData.username };

    if (formData.name !== user?.name)
      changed.name = { from: user?.name, to: formData.name };

    const currentLocation = getLocationString(user?.location);
    if (formData.location !== currentLocation)
      changed.location = { from: currentLocation, to: formData.location };

    if (formData.gender !== user?.gender)
      changed.gender = { from: user?.gender, to: formData.gender };

    if (formData.bio !== user?.bio)
      changed.bio = { from: user?.bio, to: formData.bio };

    if (formData.date_of_birth !== user?.date_of_birth)
      changed.date_of_birth = {
        from: user?.date_of_birth
          ? new Date(user.date_of_birth).toLocaleDateString("en-GB")
          : "Not set",
        to: formData.date_of_birth
          ? new Date(formData.date_of_birth).toLocaleDateString("en-GB")
          : "Not set",
      };

    // Check show_profile_public change
    // user boolean might validly be false, so check undefined
    const currentPublic = user?.show_profile_public ?? true;
    const newPublic = formData.show_profile_public ?? true;
    if (newPublic !== currentPublic) {
      changed.visibility = {
        from: currentPublic ? "Public" : "Private",
        to: newPublic ? "Public" : "Private",
      };
    }

    // NEW image changes
    if (newAvatarFile) {
      changed.avatar = { from: "Current avatar", to: "New image selected" };
    }

    if (newCoverFile) {
      changed.cover = { from: "Current cover", to: "New image selected" };
    }

    // if no fields changed
    if (Object.keys(changed).length === 0) {
      toast.warn("No changes detected.");
      return;
    }

    setChanges(changed);
    setShowConfirmModal(true);
  };

  const handleSave = async () => {
    try {
      // 1) Upload avatar if selected
      if (newAvatarFile) {
        const fd = new FormData();
        fd.append("avatar", newAvatarFile);

        await api.post("/users/me/avatar", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // 2) Upload cover if selected
      if (newCoverFile) {
        const fd = new FormData();
        fd.append("cover", newCoverFile);

        await api.post("/users/me/cover", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      // Basic validation
      if (!/^[a-z0-9_]{3,30}$/i.test(formData.username)) {
        toast.error("Invalid username format.");
        return;
      }

      const payload = {
        username: formData.username?.trim().toLowerCase(),
        name: formData.name || "",
        location: formData.location || "",
        date_of_birth: formData.date_of_birth || "", // safe fallback
        bio: formData.bio || "",
        gender: formData.gender || "",
      };

      const res = await api.put("/users/me", payload);
      toast.success(res.data.message || "Profile updated successfully!");

      // NEW — refresh global user & stats
      await dispatch(fetchUserProfile());
      // Clear selected file states
      setNewAvatarFile(null);
      setNewCoverFile(null);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update profile.");
    }
  };

  // --- DELETE AVATAR ---
  const handleDeleteAvatar = async () => {
    try {
      const res = await api.delete("/users/me/avatar");
      toast.success(res.data.message || "Avatar removed");

      setAvatarPreview(""); // Clear preview
      setNewAvatarFile(null);

      await dispatch(fetchUserProfile()); // refresh user
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete avatar");
    }
  };

  // --- DELETE COVER ---
  const handleDeleteCover = async () => {
    try {
      const res = await api.delete("/users/me/cover");
      toast.success(res.data.message || "Cover photo removed");

      setCoverPreview("");
      setNewCoverFile(null);

      await dispatch(fetchUserProfile());
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete cover photo");
    }
  };

  const tabs = [
    { key: "profile", label: "Profile Info", icon: <User /> },
    { key: "preferences", label: "Preferences", icon: <Moon /> },
    { key: "membership", label: "Membership", icon: <CreditCard /> },
    { key: "contribute", label: "Contribute", icon: <Send /> },
    { key: "account", label: "Privacy & Account", icon: <Lock /> },
  ];

  const renderSection = () => {
    switch (activeTab) {
      case "profile":
        return (
          <ProfileInfo
            formData={formData}
            setFormData={setFormData}
            user={user}
            avatarPreview={avatarPreview}
            coverPreview={coverPreview}
            newAvatarFile={newAvatarFile}
            newCoverFile={newCoverFile}
            handleAvatarUpload={handleAvatarUpload}
            handleCoverUpload={handleCoverUpload}
            handleDeleteAvatar={handleDeleteAvatar}
            handleDeleteCover={handleDeleteCover}
            reviewChangesBeforeSave={reviewChangesBeforeSave}
          />
        );

      case "preferences":
        return <Preferences formData={formData} setFormData={setFormData} />;

      case "membership":
        return <Membership />;

      case "contribute":
        return <Contribute />;

      case "account":
        return <Account />;

      default:
        return null;
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      // Use standard background, removing internal paddings that might be double-applied
      className="min-h-screen bg-transparent transition-colors py-6"
    >
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 md:items-start">
        {/* Sidebar */}
        <aside className="w-full md:w-72 flex-shrink-0 z-40">
          {/* Mobile Toggle Button */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden w-auto self-start flex items-center gap-3 px-6 py-3 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="font-semibold text-gray-800 dark:text-gray-200">
              {tabs.find((t) => t.key === activeTab)?.label || "Settings"}
            </span>
            <ChevronRight className="w-5 h-5 text-gray-500" />
          </button>

          {/* Backdrop for Mobile */}
          <AnimatePresence>
            {isSidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSidebarOpen(false)}
                className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
              />
            )}
          </AnimatePresence>

          {/* Sidebar Content (Drawer on Mobile / Sticky Card on Desktop) */}
          <div
            className={`fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-gray-800 md:bg-white md:dark:bg-gray-800 shadow-2xl md:shadow-sm border-r md:border border-gray-100 dark:border-gray-700 transform transition-transform duration-300 ease-in-out ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 md:rounded-3xl md:block md:sticky md:top-24`}
          >
            {/* Mobile Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 md:hidden">
              <span className="font-bold text-lg text-gray-800 dark:text-gray-100">
                Settings
              </span>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full"
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300 rotate-180" />
              </button>
            </div>

            {/* Desktop Header */}
            <div className="hidden md:block p-6 pb-2">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                Settings
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your account
              </p>
            </div>

            {/* Tabs List */}
            <div className="flex flex-col p-3 gap-2">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setIsSidebarOpen(false);
                    }}
                    className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-left font-medium transition-all duration-200 group ${
                      isActive
                        ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 shadow-sm"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`transition-colors duration-200 ${
                          isActive
                            ? "text-green-600 dark:text-green-400"
                            : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                        }`}
                      >
                        {React.cloneElement(tab.icon, { size: 20 })}
                      </span>
                      <span className="text-sm md:text-base tracking-wide">
                        {tab.label}
                      </span>
                    </div>
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active"
                        className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-400"
                      />
                    )}
                  </button>
                );
              })}

              {/* Admin Portal Link - Only for Admins */}
              {user?.role === "admin" && (
                <>
                  <div className="my-1 border-t border-gray-100 dark:border-gray-700 mx-4"></div>
                  <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-left font-medium transition-all duration-200 group text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200">
                        <Shield size={20} />
                      </span>
                      <span className="text-sm md:text-base tracking-wide">
                        Admin Portal
                      </span>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 border border-gray-100 dark:border-gray-700">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
            >
              {renderSection()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      {/* SAVE CONFIRMATION MODAL */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl w-full max-w-md popup-anim">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Confirm Changes
            </h2>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              You are updating the following fields:
            </p>

            <div className="mb-4 space-y-2">
              {Object.entries(changes).map(([key, val]) => (
                <div
                  key={key}
                  className="text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 p-2 rounded-md"
                >
                  <strong className="capitalize">{key}</strong>
                  <br />
                  <span className="text-gray-500 text-xs">From:</span>{" "}
                  {val.from}
                  <br />
                  <span className="text-gray-500 text-xs">To:</span> {val.to}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 btn-anim"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
                className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 btn-anim"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CROPPER MODAL */}
      {imageToCrop && (
        <ImageCropper
          imageSrc={imageToCrop}
          aspectRatio={cropType === "avatar" ? 1 : 3}
          onCropDone={handleCropDone}
          onCancel={() => {
            setImageToCrop(null);
            setCropType(null);
          }}
        />
      )}
    </motion.div>
  );
};

export default Settings;
